import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabase';

interface UnreadMessagesState {
  hasUnread: boolean;
  unreadCount: number;
  markAllRead: () => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
}

export const useUnreadMessages = (): UnreadMessagesState => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasUnread, setHasUnread] = useState(false);
  const { user, userRole } = useAuth();

  // Make checkForUnreadMessages accessible
  const checkForUnreadMessages = async () => {
    if (!user) return;
    try {
      // Determine the user role to check the right messages
      const isAdmin = userRole === 'bizadmin' || userRole === 'owner' || userRole === 'staff';
      const senderTypeToCheck = isAdmin ? 'parent' : 'staff';
      // Fetch all messages with their message_reads join
      const { data: messages, error } = await supabase
        .from('messages')
        .select('id, sender_type, message_reads(user_id)')
        .eq('sender_type', senderTypeToCheck);
      if (error) throw error;
      // Count messages where message_reads does not include the current user
      const unreadMessagesCount = (messages || []).filter(msg =>
        !msg.message_reads.some(r => r.user_id === user.id)
      ).length;
      setUnreadCount(unreadMessagesCount);
      setHasUnread(unreadMessagesCount > 0);
    } catch (error) {
      console.error('Error checking for unread messages:', error);
    }
  };

  useEffect(() => {
    if (!user) return;
    // Check immediately
    checkForUnreadMessages();
    // Set up polling every 60 seconds
    const intervalId = setInterval(checkForUnreadMessages, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, [user, userRole]);

  const markAllRead = async (): Promise<void> => {
    if (!user) return;
    
    try {
      // Get all unread message IDs for this user
      const { data: unreadMessages, error: fetchError } = await supabase
        .from('messages')
        .select('id')
        .not('id', 'in', supabase
          .from('message_reads')
          .select('message_id')
          .eq('user_id', user.id)
        );
      
      if (fetchError) throw fetchError;
      
      if (unreadMessages && unreadMessages.length > 0) {
        // Create message_reads entries for all unread messages
        const messagesToMark = unreadMessages.map(msg => ({
          message_id: msg.id,
          user_id: user.id
        }));
        
        const { error: markError } = await supabase
          .from('message_reads')
          .upsert(messagesToMark, { onConflict: 'message_id,user_id' });
          
        if (markError) throw markError;
      }
      
      await checkForUnreadMessages();
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  const markAsRead = async (messageId: string): Promise<void> => {
    if (!user) return;
    
    try {
      // Check if this message is already marked as read
      const { data: existingRead, error: checkError } = await supabase
        .from('message_reads')
        .select()
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (checkError) throw checkError;
      
      // If not already read, mark it as read
      if (!existingRead) {
        const { error: insertError } = await supabase
          .from('message_reads')
          .insert({
            message_id: messageId,
            user_id: user.id
          });
          
        if (insertError) throw insertError;
        
        // Refetch unread count after marking as read
        await checkForUnreadMessages();
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  return {
    hasUnread,
    unreadCount,
    markAllRead,
    markAsRead
  };
};
